/**
 * HÀNG ĐỢI CHẤM CÔNG OFFLINE — docs/01 §1.8, docs/02 §2.6
 *
 * NGUYÊN TẮC BẤT DI BẤT DỊCH:
 *   Mọi lần chấm công LUÔN vào hàng đợi trước, rồi mới thử gửi.
 *   Nhân viên không bao giờ thấy màn hình lỗi mạng — họ thấy
 *   "Đã ghi nhận, đang đồng bộ".
 *
 * Chống nhân đôi giờ công có hai lớp, lớp này là lớp thứ nhất:
 *   1. client_uuid sinh MỘT LẦN lúc xếp hàng, giữ nguyên qua mọi lần thử lại
 *   2. UNIQUE (employee_id, punch_at, direction) ở PostgreSQL
 * Bấm đồng bộ mười lần vẫn ra một bản ghi.
 */

import Dexie, { type Table } from 'dexie'

import { layToken, lamMoiToken } from '../api/client'
import { uuidV4 } from './uuid'

export type Direction = 'in' | 'out'

export type PunchMethod =
  | 'wifi_ip' | 'wifi_mobile' | 'totp_offline'
  | 'qr_supervisor' | 'gps_photo' | 'device' | 'manual'

export type QueueState = 'pending' | 'sending' | 'synced' | 'failed'

export interface QueuedPunch {
  /** Khoá chống gửi trùng. Sinh một lần, KHÔNG BAO GIỜ sinh lại khi thử lại. */
  client_uuid: string
  direction: Direction
  method: PunchMethod
  /** Giờ đồng hồ máy lúc bấm nút. Server so lệch với giờ thật (docs/03 E10). */
  device_clock_ts: number
  queued_at: number

  token?: string | null       // token HMAC từ beacon
  nonce?: string | null
  location_id?: number | null
  gps?: { lat: number; lng: number; accuracy?: number } | null
  bssid?: string | null
  device_id?: string | null
  /** Mã 6 số của tổ trưởng (docs/02 §2.4) hoặc tablet lễ tân (§2.3) */
  otp_code?: string | null
  session_id?: number | null
  photo?: Blob | null
  /**
   * Công trình nhân viên tự chọn lúc chụp ảnh, và việc đang làm.
   *
   * Dành cho nơi máy không lấy được toạ độ (hầm, nhà xưởng, tầng thấp). Đây
   * là LỜI KHAI: server chỉ dùng khi GPS không xác định được gì, và bản ghi
   * kiểu đó luôn phải qua tay quản lý.
   */
  site_declared_id?: number | null
  ghi_chu?: string | null
  /**
   * Ca làm việc nhân viên chọn trên màn chấm công (migration 009).
   *
   * Xếp vào hàng đợi cùng bản ghi để bản chấm công offline giữ đúng ca đã
   * chọn LÚC BẤM — đồng bộ ba ngày sau, admin có đổi danh sách ca thì bản ghi
   * vẫn mang ca cũ. Server kiểm tra ca còn hiệu lực; không hợp lệ thì bỏ
   * trống chứ không từ chối bản ghi.
   */
  shift_id?: number | null

  is_mock_location: boolean
  is_rooted: boolean

  state: QueueState
  attempts: number
  next_attempt_at: number
  last_error?: string
}

/** Thử lại theo cấp số nhân: 30s · 2p · 10p · 1h (docs/02 §2.6). */
const BACKOFF_MS = [30_000, 120_000, 600_000, 3_600_000] as const

function backoffFor(attempts: number): number {
  return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)]
}

class AttendanceDB extends Dexie {
  punches!: Table<QueuedPunch, string>

  constructor() {
    super('chamcong')
    this.version(1).stores({
      // client_uuid là khoá chính → Dexie tự chặn xếp hàng trùng
      punches: 'client_uuid, state, next_attempt_at, queued_at',
    })
  }
}

export const db = new AttendanceDB()

/**
 * Xếp một lần chấm công vào hàng đợi. Trả về ngay, KHÔNG chờ mạng.
 * Đây là hàm màn hình chính gọi khi nhân viên bấm nút.
 */
export async function enqueue(
  punch: Omit<QueuedPunch,
    'client_uuid' | 'state' | 'attempts' | 'next_attempt_at' | 'queued_at'>,
): Promise<string> {
  const now = Date.now()
  const item: QueuedPunch = {
    ...punch,
    client_uuid: uuidV4(),
    state: 'pending',
    attempts: 0,
    next_attempt_at: now,
    queued_at: now,
  }
  await db.punches.add(item)
  return item.client_uuid
}

/** Các bản ghi đến hạn thử gửi. */
export async function due(now = Date.now()): Promise<QueuedPunch[]> {
  return db.punches
    .where('state').anyOf('pending', 'failed')
    .filter((p) => p.next_attempt_at <= now)
    .sortBy('queued_at')
}

export async function pendingCount(): Promise<number> {
  return db.punches.where('state').anyOf('pending', 'failed', 'sending').count()
}

/** Lỗi server đã kết luận dứt khoát cho một bản ghi (4xx, không thử lại). */
export interface LoiDongBo {
  client_uuid: string
  code: string
  message: string
  /** Server nói cụ thể nên làm gì (ví dụ "Ảnh bị mờ. Giữ máy chắc tay rồi chụp lại.") */
  huong_dan?: string
}

/**
 * Đẩy hàng đợi lên server.
 *
 * Xử lý DUPLICATE_PUNCH: server trả HTTP 200 chứ KHÔNG phải lỗi. Client
 * PHẢI coi là thành công và xoá khỏi hàng đợi — nếu không sẽ lặp vô hạn
 * (docs/05 §5.3).
 *
 * `loi` liệt kê các bản ghi bị từ chối dứt khoát trong lượt này, để màn hình
 * chấm công báo ngay cho nhân viên (ảnh mờ, mặt không khớp…) thay vì im lặng.
 */
export async function flush(apiBase = '/api/v1'): Promise<{
  synced: number
  failed: number
  loi: LoiDongBo[]
}> {
  if (!navigator.onLine) return { synced: 0, failed: 0, loi: [] }

  const batch = await due()
  let synced = 0
  let failed = 0
  const loi: LoiDongBo[] = []

  for (const item of batch) {
    await db.punches.update(item.client_uuid, { state: 'sending' })
    try {
      let res = await postPunch(apiBase, item)
      let body = await res.json().catch(() => ({}))
      let code: string | undefined = body?.error?.code ?? body?.code

      // Access token chỉ sống 15 phút. Bản ghi xếp hàng lúc mất sóng rồi đồng
      // bộ sau vài giờ sẽ gặp 403 — làm mới token rồi gửi lại MỘT lần, không
      // được coi đó là lỗi vĩnh viễn (giờ công của nhân viên nằm trong đó).
      if (res.status === 403 && code === 'FORBIDDEN' && (await lamMoiToken())) {
        res = await postPunch(apiBase, item)
        body = await res.json().catch(() => ({}))
        code = body?.error?.code ?? body?.code
      }

      // 2xx, hoặc mã DUPLICATE_PUNCH → coi như thành công
      if (res.ok || code === 'DUPLICATE_PUNCH') {
        await db.punches.delete(item.client_uuid)
        synced++
        continue
      }

      // Lỗi 4xx không phải do mạng: không thử lại vô ích, giữ để người dùng thấy.
      // Trừ 403 (hết phiên đăng nhập) — đăng nhập lại là gửi được, giữ để thử lại.
      const permanent = res.status >= 400 && res.status < 500
        && res.status !== 429 && res.status !== 403
      await db.punches.update(item.client_uuid, {
        state: 'failed',
        attempts: item.attempts + 1,
        last_error: code ?? `HTTP ${res.status}`,
        next_attempt_at: permanent
          ? Number.MAX_SAFE_INTEGER          // chờ người dùng xử lý tay
          : Date.now() + backoffFor(item.attempts),
      })
      if (permanent) {
        loi.push({
          client_uuid: item.client_uuid,
          code: code ?? `HTTP_${res.status}`,
          message: body?.error?.message ?? '',
          huong_dan: body?.error?.detail?.huong_dan ?? undefined,
        })
      }
      failed++
    } catch (e) {
      // Mất mạng giữa chừng — thử lại theo cấp số nhân
      await db.punches.update(item.client_uuid, {
        state: 'failed',
        attempts: item.attempts + 1,
        last_error: e instanceof Error ? e.message : 'Lỗi mạng',
        next_attempt_at: Date.now() + backoffFor(item.attempts),
      })
      failed++
    }
  }

  return { synced, failed, loi }
}

async function postPunch(apiBase: string, item: QueuedPunch): Promise<Response> {
  const payload = {
    client_uuid: item.client_uuid,
    direction: item.direction,
    method: item.method,
    token: item.token ?? null,
    nonce: item.nonce ?? null,
    location_id: item.location_id ?? null,
    gps: item.gps ?? null,
    bssid: item.bssid ?? null,
    device_id: item.device_id ?? null,
    otp_code: item.otp_code ?? null,
    session_id: item.session_id ?? null,
    site_declared_id: item.site_declared_id ?? null,
    ghi_chu: item.ghi_chu ?? null,
    shift_id: item.shift_id ?? null,
    is_mock_location: item.is_mock_location,
    is_rooted: item.is_rooted,
    device_clock_ts: item.device_clock_ts,
    // Giờ máy LÚC GỬI. Server so với device_clock_ts (lúc bấm) để biết bản
    // ghi đã nằm trong hàng đợi bao lâu → suy ra giờ bấm thật, kể cả khi
    // đồng hồ máy sai (docs/02 §2.6, services/dong_bo.py).
    sent_at_ts: Math.floor(Date.now() / 1000),
  }

  // Đọc token LÚC GỬI, không phải lúc xếp hàng: bản ghi có thể nằm trong hàng
  // đợi nhiều giờ, token lúc đó đã được làm mới.
  const headers: Record<string, string> = {}
  const token = layToken()
  if (token) headers.Authorization = `Bearer ${token}`

  if (item.photo) {
    // Cùng một URL với JSON — server nhận cả hai kiểu (docs/10 P0-1).
    // KHÔNG đặt Content-Type: trình duyệt tự sinh boundary cho multipart.
    const form = new FormData()
    form.append('payload', JSON.stringify(payload))
    form.append('photo', item.photo, `${item.client_uuid}.webp`)
    return fetch(`${apiBase}/attendance/check-in`, { method: 'POST', headers, body: form })
  }

  return fetch(`${apiBase}/attendance/check-in`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

/** Gọi khi app khởi động: đẩy hàng đợi mỗi khi có mạng trở lại. */
export function startAutoSync(apiBase = '/api/v1', intervalMs = 30_000): () => void {
  const tick = () => { void flush(apiBase) }
  window.addEventListener('online', tick)
  const timer = window.setInterval(tick, intervalMs)
  tick()
  return () => {
    window.removeEventListener('online', tick)
    window.clearInterval(timer)
  }
}
